import { useEffect, useReducer } from 'react';
import type { Dispatch, ReactNode } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { KYC_STATUS } from '../types/kyc';
import type { KYCStatus, DocType, DocumentScore, KYCSession, KYCResult } from '../types/kyc';
import type { RejectionReason } from '../constants/rejectionMessages';
import { REJECTION_REASON } from '../constants/rejectionMessages';
import { useKYCStream, STREAM_EVENT_TYPE } from '../hooks/useKYCStream';
import type { StreamEvent, ScorePayload, IneBackRequiredPayload } from '../hooks/useKYCStream';
import { useSimulatedStream } from '../hooks/useSimulatedStream';
import { useSimulation } from '../contexts/SimulationContext';
import { CaptureIDStep } from './steps/CaptureIDStep';
import { CaptureINEBackStep } from './steps/CaptureINEBackStep';
import { CapturePOAStep } from './steps/CapturePOAStep';
import { ValidationProgress } from './steps/ValidationProgress';
import { CompletionScreen } from './steps/CompletionScreen';
import { RejectionScreen } from './steps/RejectionScreen';
import { ConditionalScreen } from './steps/ConditionalScreen';
import { HumanReviewScreen } from './steps/HumanReviewScreen';
import { CompletedPreviousScreen } from './steps/CompletedPreviousScreen';
import { UserInHumanReviewScreen } from './steps/UserInHumanReviewScreen';
import { ResumingSessionScreen } from './steps/ResumingSessionScreen';
import { PreviousFailureScreen } from './steps/PreviousFailureScreen';
import { LivenessStep } from './steps/LivenessStep';
import {
  submitHumanReview,
  executeWorkflow,
  getUserKycStatus,
  startKycProcess,
  resetKycProcess,
} from '../services/kycApi';
import type { KycLastResult, UserKycStatusResponse } from '../services/kycApi';
import { useResumePolling } from '../hooks/useResumePolling';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos del estado y acciones de la máquina de estados
// ─────────────────────────────────────────────────────────────────────────────

// Estado completo del flujo KYC en un momento dado
interface KYCFlowState {
  status: KYCStatus;
  session: KYCSession;
  idDocType: DocType | null;
  idRetries: number;
  poaRetries: number;
  livenessRetries: number;
  idScore: DocumentScore | null;
  poaScore: DocumentScore | null;
  livenessScore: number | null;
  requiresPOA: boolean;             // true si INE sin dirección o Pasaporte
  livenessEnabled: boolean;         // feature flag — desactivado en fase 1
  // Datos del anverso de la INE cuando el pipeline detectó que falta el reverso.
  // Ambos campos son null si el usuario no ha pasado por CAPTURING_INE_BACK.
  ineFilenameFront: string | null;
  ineProcessIdFront: string | null;
  // Nombre del archivo definitivo del documento de identidad en GCS.
  // Para pasaporte o INE en un solo PDF: filename del upload original.
  // Para INE con anverso y reverso separados: filename del PDF fusionado (merge-ine).
  // Usado por el liveness-service para la comparación facial (face matching).
  finalDocFilename: string | null;
  // D-6: acumula true si cualquier evento score_complete durante el flujo indicó
  // requiresHumanReview=true. Se evalúa al recibir LIVENESS_SUCCESS para determinar
  // si la sesión debe transicionar a PENDING_HUMAN_REVIEW en lugar de COMPLETED.
  requiresHumanReview: boolean;
  rejectionReason: RejectionReason | null;
  errorMessage: string | null;
  // Output del classify persistido para encadenar el pipeline extraction → validation → storage
  // en cuanto el score_complete confirma que el documento pasó la clasificación.
  classifyOutput: { processId: string; filename: string; documents: unknown[] } | null;
  // Output del extraction necesario para construir el payload de storage (documents extraídos).
  extractionOutput: { processId: string; filename: string; documents: unknown[] } | null;
  // D-7: identificador del usuario para gestión de estado persistente entre sesiones.
  userId: string | null;
  // D-7: resultado del último proceso KYC terminado, leído de kyc_users en CHECKING_STATUS.
  // Presente únicamente cuando status === COMPLETED_PREVIOUS.
  previousKycResult: KycLastResult | null;
  // D-7: true si el usuario puede reiniciar su proceso completado (ALLOW_KYC_RESET=true en backend).
  canReset: boolean;
  // D-8: session_id del proceso in_progress detectado en CHECKING_STATUS.
  // Presente solo durante RESUMING_SESSION; null en todos los demás estados.
  resumingProcessId: string | null;
}

// Unión discriminada de todas las acciones posibles sobre el estado
type KYCAction =
  | { type: 'START_CAPTURE_ID' }
  | { type: 'SET_ID_DOC_TYPE'; docType: DocType }
  | { type: 'ID_VALIDATION_SUCCESS'; score: DocumentScore; requiresPOA: boolean }
  | { type: 'ID_VALIDATION_FAILED'; reason: RejectionReason }
  | { type: 'START_CAPTURE_POA' }
  | { type: 'POA_VALIDATION_SUCCESS'; score: DocumentScore }
  | { type: 'POA_VALIDATION_FAILED'; reason: RejectionReason }
  | { type: 'POA_VALIDATION_CONDITIONAL'; score: DocumentScore }
  | { type: 'START_LIVENESS' }
  | { type: 'LIVENESS_SUCCESS'; score: number }
  | { type: 'LIVENESS_FAILED'; reason: RejectionReason }
  | { type: 'COMPLETE' }
  | { type: 'REJECT'; reason: RejectionReason }
  | { type: 'SET_ERROR'; message: string }
  | { type: 'RETRY_ID' }
  | { type: 'RETRY_POA' }
  | { type: 'RETRY_LIVENESS' }
  | { type: 'SUBMIT_POA' }
  // Acción D-6.5: guarda los datos del anverso sin cambiar el status visible al usuario.
  // Se despacha en CaptureIDStep inmediatamente después de subir el archivo, antes de
  // transicionar a VALIDATING_ID, para que el filename quede disponible cuando llegue
  // el evento ine_back_required desde el SSE.
  | { type: 'SET_INE_FRONT_DATA'; processId: string; filename: string }
  // Acción D-6.5: transición a CAPTURING_INE_BACK disparada por el evento SSE ine_back_required.
  | { type: 'INE_BACK_REQUIRED' }
  // Acción D-6: marca que al menos un documento del flujo requiere revisión humana asíncrona.
  // Se despacha por cada evento score_complete que llega con requiresHumanReview=true.
  // Acumulativa: una vez en true, permanece true hasta el fin de la sesión.
  | { type: 'ACCUMULATE_HUMAN_REVIEW' }
  // Guarda el filename definitivo del documento de identidad para la comparación facial.
  // Para pasaporte / INE en un solo PDF: se despacha desde CaptureIDStep tras el upload.
  // Para INE con reverso separado: se re-despacha desde CaptureINEBackStep con el PDF fusionado.
  | { type: 'SET_FINAL_DOC_FILENAME'; filename: string }
  // Persiste el output del classify para encadenar extraction → validation → storage tras liveness.
  | { type: 'SAVE_CLASSIFY_OUTPUT'; processId: string; filename: string; documents: unknown[] }
  // Persiste el output del extraction para construir el payload de storage.
  | { type: 'SAVE_EXTRACTION_OUTPUT'; processId: string; filename: string; documents: unknown[] }
  // Actualiza el score de ID con el valor real del ai-service (Fix 2).
  // Se despacha al recibir score_update desde el pipeline server-side.
  | { type: 'UPDATE_SCORE'; score: number }
  // D-7: resultado de la verificación de estado del usuario en CHECKING_STATUS.
  // USER_STATUS_RESOLVED → el usuario puede iniciar un proceso nuevo (none/rejected/error).
  | { type: 'USER_STATUS_RESOLVED' }
  // USER_STATUS_BLOCKED_ACTIVE → tiene un proceso in_progress; entra a RESUMING_SESSION.
  | { type: 'USER_STATUS_BLOCKED_ACTIVE'; activeProcessId: string | null }
  // RESUME_AT_POA → el widget se reabrió mientras el flujo esperaba el comprobante de domicilio.
  // Salta directamente a CAPTURING_POA sin pasar por RESUMING_SESSION.
  | { type: 'RESUME_AT_POA' }
  // USER_STATUS_BLOCKED_REVIEW → proceso en revisión humana; no puede iniciar uno nuevo.
  | { type: 'USER_STATUS_BLOCKED_REVIEW'; activeProcessId: string | null }
  // D-8: el usuario eligió no esperar la resolución del proceso in_progress (botón "dar por terminado")
  // O el polling alcanzó el timeout. En ambos casos el flujo vuelve a IDLE para iniciar una nueva captura.
  | { type: 'RESUME_GIVE_UP' }
  // USER_STATUS_COMPLETED → ya tiene un proceso completado; se muestra el resultado anterior.
  | { type: 'USER_STATUS_COMPLETED'; lastResult: KycLastResult | null; canReset: boolean }
  // El intento anterior terminó en rejected o error — muestra pantalla informativa antes
  // de permitir un nuevo intento, para que el usuario sepa que hubo un fallo previo.
  | { type: 'USER_STATUS_PREVIOUS_FAILURE' }
  // D-7: el pipeline server-side (extraction → validation) completó con éxito.
  // Se despacha al recibir validation_complete mientras status === AWAITING_EXTRACTION_SCORE.
  | { type: 'EXTRACTION_PIPELINE_SUCCESS' }
  // El comprobante de domicilio pasó la clasificación (score OK) — guardar score y
  // lanzar el pipeline completo de validación (freshness). El resultado definitivo
  // llega como validation_complete o extraction_rejected desde el pipeline.
  | { type: 'POA_CLASSIFICATION_SUCCESS'; score: DocumentScore }
  // D-7: el usuario confirmó reiniciar su proceso completado (requiere ALLOW_KYC_RESET=true).
  | { type: 'RESET_KYC' }
  // BUG-WIDGET-001: el usuario reabre el widget con un proceso en awaiting_liveness.
  // El pipeline de documentos ya completó — ir directo a la prueba de vida.
  | { type: 'RESUME_LIVENESS' };

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const MAX_RETRIES = 3;

// Umbrales de score para el comprobante de domicilio (según RFI Amarello)
const POA_SCORE_APPROVED = 60;
const POA_SCORE_CONDITIONAL = 40;

const INTEGRATION_API_URL = process.env.REACT_APP_INTEGRATION_API_URL ?? '';

// ─────────────────────────────────────────────────────────────────────────────
// Estado inicial a partir de la sesión recibida del Gateway
// ─────────────────────────────────────────────────────────────────────────────

function buildInitialState(session: KYCSession): KYCFlowState {
  return {
    // D-7: si la sesión incluye userId, verificar estado previo antes de capturar documentos
    status: session.userId ? KYC_STATUS.CHECKING_STATUS : KYC_STATUS.IDLE,
    session,
    idDocType: null,
    idRetries: 0,
    poaRetries: 0,
    livenessRetries: 0,
    idScore: null,
    poaScore: null,
    livenessScore: null,
    requiresPOA: false,
    livenessEnabled: session.livenessEnabled ?? false,
    ineFilenameFront: null,
    ineProcessIdFront: null,
    finalDocFilename: null,
    requiresHumanReview: false,
    rejectionReason: null,
    errorMessage: null,
    classifyOutput: null,
    extractionOutput: null,
    userId: session.userId ?? null,
    previousKycResult: null,
    canReset: false,
    resumingProcessId: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Reducer: transiciones de estado puras sin efectos secundarios
// ─────────────────────────────────────────────────────────────────────────────

function kycReducer(state: KYCFlowState, action: KYCAction): KYCFlowState {
  switch (action.type) {
    case 'START_CAPTURE_ID':
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_ID,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'SET_ID_DOC_TYPE':
      return {
        ...state,
        idDocType: action.docType,
        status: KYC_STATUS.VALIDATING_ID,
      };

    case 'ID_VALIDATION_SUCCESS':
      // D-7: la clasificación pasó — ir a AWAITING_EXTRACTION_SCORE para que el pipeline
      // server-side valide la calidad real del documento antes de avanzar a POA / liveness.
      // requiresPOA y livenessEnabled se preservan para usarlos en EXTRACTION_PIPELINE_SUCCESS.
      return {
        ...state,
        status: KYC_STATUS.AWAITING_EXTRACTION_SCORE,
        idScore: action.score,
        requiresPOA: action.requiresPOA,
        rejectionReason: null,
      };

    case 'ID_VALIDATION_FAILED': {
      const newRetries = state.idRetries + 1;
      // Si se agotaron los reintentos, rechazar definitivamente
      if (newRetries >= MAX_RETRIES) {
        return {
          ...state,
          status: KYC_STATUS.REJECTED,
          idRetries: newRetries,
          rejectionReason: REJECTION_REASON.MAX_RETRIES,
        };
      }
      return {
        ...state,
        status: KYC_STATUS.REJECTED,
        idRetries: newRetries,
        rejectionReason: action.reason,
      };
    }

    case 'POA_CLASSIFICATION_SUCCESS':
      // El score del POA es aceptable — activar el pipeline de validación completa
      // (extracción + frescura) antes de confirmar el resultado al usuario.
      return {
        ...state,
        status: KYC_STATUS.AWAITING_POA_PIPELINE,
        poaScore: action.score,
        rejectionReason: null,
      };

    case 'START_CAPTURE_POA':
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_POA,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'POA_VALIDATION_SUCCESS': {
      let nextStatus: KYCStatus;
      if (state.livenessEnabled) {
        nextStatus = KYC_STATUS.CAPTURING_LIVENESS;
      } else {
        nextStatus = KYC_STATUS.COMPLETED;
      }
      return {
        ...state,
        status: nextStatus,
        poaScore: action.score,
        rejectionReason: null,
      };
    }

    case 'POA_VALIDATION_FAILED': {
      const newRetries = state.poaRetries + 1;
      if (newRetries >= MAX_RETRIES) {
        return {
          ...state,
          status: KYC_STATUS.REJECTED,
          poaRetries: newRetries,
          rejectionReason: REJECTION_REASON.MAX_RETRIES,
        };
      }
      return {
        ...state,
        status: KYC_STATUS.REJECTED,
        poaRetries: newRetries,
        rejectionReason: action.reason,
      };
    }

    case 'POA_VALIDATION_CONDITIONAL':
      // Comprobante con score bajo: aprobar condicionalmente + alerta fraude (no rechazar)
      return {
        ...state,
        status: KYC_STATUS.CONDITIONAL,
        poaScore: action.score,
        rejectionReason: null,
      };

    case 'START_LIVENESS':
      return { ...state, status: KYC_STATUS.CAPTURING_LIVENESS };

    case 'LIVENESS_SUCCESS':
      // D-6: si algún documento acumuló requiresHumanReview durante el flujo,
      // transicionar a PENDING_HUMAN_REVIEW en lugar de COMPLETED.
      // El KYCFlowController dispara el POST /kyc/human-review/{session_id} como efecto.
      return {
        ...state,
        status: state.requiresHumanReview
          ? KYC_STATUS.PENDING_HUMAN_REVIEW
          : KYC_STATUS.COMPLETED,
        livenessScore: action.score,
        rejectionReason: null,
      };

    case 'LIVENESS_FAILED': {
      const newRetries = state.livenessRetries + 1;
      if (newRetries >= MAX_RETRIES) {
        return {
          ...state,
          status: KYC_STATUS.REJECTED,
          livenessRetries: newRetries,
          rejectionReason: REJECTION_REASON.MAX_RETRIES,
        };
      }
      return {
        ...state,
        status: KYC_STATUS.REJECTED,
        livenessRetries: newRetries,
        rejectionReason: action.reason,
      };
    }

    case 'COMPLETE':
      return { ...state, status: KYC_STATUS.COMPLETED };

    case 'REJECT':
      return { ...state, status: KYC_STATUS.REJECTED, rejectionReason: action.reason };

    case 'SET_ERROR':
      return { ...state, errorMessage: action.message };

    case 'RETRY_ID':
      // Regresa a captura de ID conservando el contador de reintentos
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_ID,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'RETRY_POA':
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_POA,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'RETRY_LIVENESS':
      // Regresa al paso de liveness conservando el contador de reintentos
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_LIVENESS,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'SUBMIT_POA':
      // Transiciona a VALIDATING_POA — el SSE stream emitirá el resultado del scoring
      return { ...state, status: KYC_STATUS.VALIDATING_POA };

    case 'SET_INE_FRONT_DATA':
      // Solo persiste los datos del anverso; el status permanece igual (VALIDATING_ID).
      // No debe cambiar el status porque el pipeline aún no confirmó si falta el reverso.
      return {
        ...state,
        ineFilenameFront: action.filename,
        ineProcessIdFront: action.processId,
      };

    case 'INE_BACK_REQUIRED':
      // El workflows-handler detectó INE con 1 sola página → solicitar reverso al usuario.
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_INE_BACK,
        rejectionReason: null,
        errorMessage: null,
      };

    case 'SET_FINAL_DOC_FILENAME':
      // Persiste el filename del documento definitivo para la comparación facial en liveness.
      // Para INE con reverso separado este dispatch sobrescribe al inicial de CaptureIDStep
      // con el filename del PDF fusionado, que es el que contiene ambas caras.
      return {
        ...state,
        finalDocFilename: action.filename,
      };

    case 'ACCUMULATE_HUMAN_REVIEW':
      // Una vez marcado como true, el flag no puede volver a false en la misma sesión.
      return {
        ...state,
        requiresHumanReview: true,
      };

    case 'SAVE_CLASSIFY_OUTPUT':
      return {
        ...state,
        classifyOutput: {
          processId: action.processId,
          filename: action.filename,
          documents: action.documents,
        },
      };

    case 'SAVE_EXTRACTION_OUTPUT':
      return {
        ...state,
        extractionOutput: {
          processId: action.processId,
          filename: action.filename,
          documents: action.documents,
        },
      };

    case 'UPDATE_SCORE':
      // Durante AWAITING_POA_PIPELINE el score_update pertenece al POA, no al ID.
      if (state.status === KYC_STATUS.AWAITING_POA_PIPELINE) {
        if (!state.poaScore) return state;
        return { ...state, poaScore: { ...state.poaScore, score: action.score } };
      }
      if (!state.idScore) return state;
      return {
        ...state,
        idScore: { ...state.idScore, score: action.score },
      };

    // D-7: transiciones desde CHECKING_STATUS
    case 'USER_STATUS_RESOLVED':
      // Estado none/rejected/error → puede iniciar; va a IDLE para que el autostart lo active.
      return {
        ...state,
        status: KYC_STATUS.IDLE,
        previousKycResult: null,
        canReset: false,
      };

    case 'USER_STATUS_BLOCKED_ACTIVE':
      // D-8: proceso in_progress detectado — entrar a RESUMING_SESSION para sondear
      // getUserKycStatus hasta que el pipeline server-side resuelva el estado.
      // Se guarda activeProcessId para identificar el proceso en los logs de debug.
      return {
        ...state,
        status: KYC_STATUS.RESUMING_SESSION,
        resumingProcessId: action.activeProcessId,
        previousKycResult: null,
        canReset: false,
      };

    case 'RESUME_AT_POA':
      // El widget se reabrió mientras el flujo esperaba el comprobante de domicilio.
      // El documento de identidad ya fue aprobado (flowStep="awaiting_poa" en Firestore).
      // Se salta directo a CAPTURING_POA para que el usuario pueda subir el comprobante
      // sin tener que volver a capturar su identificación.
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_POA,
        resumingProcessId: null,
      };

    case 'RESUME_GIVE_UP':
      // El usuario eligió no esperar o el polling alcanzó el timeout.
      // Se vuelve a IDLE para iniciar una nueva captura desde cero.
      // El pipeline previo sigue corriendo en el servidor; cuando el siguiente
      // execute_pipeline se ejecute, actualizará activeProcessId en kyc_users.
      return {
        ...state,
        status: KYC_STATUS.IDLE,
        resumingProcessId: null,
        previousKycResult: null,
        canReset: false,
      };

    case 'USER_STATUS_BLOCKED_REVIEW':
      // Proceso en revisión humana — bloquear al usuario; no puede iniciar uno nuevo.
      return {
        ...state,
        status: KYC_STATUS.USER_IN_HUMAN_REVIEW,
      };

    case 'USER_STATUS_COMPLETED':
      // Proceso completado — mostrar resultado anterior; con ALLOW_KYC_RESET puede reiniciar.
      return {
        ...state,
        status: KYC_STATUS.COMPLETED_PREVIOUS,
        previousKycResult: action.lastResult,
        canReset: action.canReset,
      };

    case 'USER_STATUS_PREVIOUS_FAILURE':
      // Intento anterior rechazado o con error — mostrar pantalla informativa antes de reiniciar.
      return {
        ...state,
        status: KYC_STATUS.PREVIOUS_FAILURE,
      };

    case 'EXTRACTION_PIPELINE_SUCCESS': {
      // El pipeline backend validó el documento con éxito — avanzar al siguiente paso del flujo.
      // Usa los flags requiresPOA y livenessEnabled que se guardaron en ID_VALIDATION_SUCCESS.
      let nextStatus: KYCStatus;
      if (state.requiresPOA) {
        nextStatus = KYC_STATUS.CAPTURING_POA;
      } else if (state.livenessEnabled) {
        nextStatus = KYC_STATUS.CAPTURING_LIVENESS;
      } else {
        // Sin POA ni liveness: el pipeline ya almacenó el documento — el proceso está completo.
        nextStatus = state.requiresHumanReview
          ? KYC_STATUS.PENDING_HUMAN_REVIEW
          : KYC_STATUS.COMPLETED;
      }
      return { ...state, status: nextStatus };
    }

    case 'RESET_KYC':
      // Reinicia el flujo completo manteniendo solo los datos de sesión y userId.
      return {
        ...buildInitialState(state.session),
        userId: state.userId,
        // Sobrescribir el status inicial a IDLE — el reset ya ejecutó la API, no hay que re-verificar.
        status: KYC_STATUS.IDLE,
      };

    case 'RESUME_LIVENESS':
      // El pipeline de documentos ya completó (awaiting_liveness en el backend).
      // Ir directo a la prueba de vida; LivenessStep siempre inicia en el guide/preview.
      return {
        ...state,
        status: KYC_STATUS.CAPTURING_LIVENESS,
      };

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Devuelve el progreso de 0 a 100 según el estado actual para el LinearProgress
function getProgress(status: KYCStatus): number {
  switch (status) {
    case KYC_STATUS.IDLE:
    case KYC_STATUS.CHECKING_STATUS:
    case KYC_STATUS.RESUMING_SESSION:
    case KYC_STATUS.CAPTURING_ID:
      return 0;
    case KYC_STATUS.VALIDATING_ID:
      return 20;
    case KYC_STATUS.CAPTURING_INE_BACK:
      return 25;
    case KYC_STATUS.AWAITING_EXTRACTION_SCORE:
      return 40;
    case KYC_STATUS.CAPTURING_POA:
      return 55;
    case KYC_STATUS.VALIDATING_POA:
      return 70;
    case KYC_STATUS.CAPTURING_LIVENESS:
      return 85;
    case KYC_STATUS.COMPLETED:
    case KYC_STATUS.COMPLETED_PREVIOUS:
    case KYC_STATUS.CONDITIONAL:
    case KYC_STATUS.PENDING_HUMAN_REVIEW:
    case KYC_STATUS.USER_IN_HUMAN_REVIEW:
      return 100;
    case KYC_STATUS.REJECTED:
    case KYC_STATUS.PREVIOUS_FAILURE:
    default:
      return 0;
  }
}

// Convierte el estado interno al contrato público KYCResult para notificar al padre
function toKYCResult(state: KYCFlowState): KYCResult {
  return {
    status: state.status,
    idDocType: state.idDocType ?? undefined,
    idScore: state.idScore ?? undefined,
    poaScore: state.poaScore ?? undefined,
    livenessScore: state.livenessScore ?? undefined,
    rejectionReason: state.rejectionReason ?? undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

interface KYCFlowControllerProps {
  session: KYCSession;
  onComplete?: (result: KYCResult) => void;
  // Llamado cuando el usuario cierra explícitamente la pantalla de resultado final.
  // El padre usa este callback para cerrar el widget y actualizar su UI.
  onDismiss?: () => void;
  // Callback opcional para que el padre observe el estado interno (usado en StandaloneMode para debug)
  onStateChange?: (state: KYCFlowState) => void;
}

// Mapea strings del backend al tipo RejectionReason — fallback a DOC_LOW_SCORE
function mapToRejectionReason(reason: string | undefined): RejectionReason {
  const valid = Object.values(REJECTION_REASON) as string[];
  if (reason && valid.includes(reason)) {
    return reason as RejectionReason;
  }
  return REJECTION_REASON.DOC_LOW_SCORE;
}

export function KYCFlowController({ session, onComplete, onDismiss, onStateChange }: KYCFlowControllerProps) {
  const [state, dispatch] = useReducer(kycReducer, session, buildInitialState);

  // Lee la configuración de simulación inyectada por SimulationProvider (o el default desactivado)
  const simulation = useSimulation();

  // Traduce eventos SSE del workflows-handler a acciones del reducer.
  // Definida dentro del componente para leer state.status sin stale closure
  // (useKYCStream actualiza onEventRef en cada render).
  const handleStreamEvent = (event: StreamEvent) => {
    switch (event.type) {
      case STREAM_EVENT_TYPE.INE_BACK_REQUIRED: {
        // Guard: solo se procesa si aún estamos esperando el resultado del anverso.
        // Sin este guard, un evento SSE tardío (replay por reconexión o re-polling de Firestore)
        // revierte el flujo desde cualquier estado posterior (COMPLETED, CAPTURING_LIVENESS, etc.).
        if (state.status !== KYC_STATUS.VALIDATING_ID) break;
        const payload = event.payload as unknown as IneBackRequiredPayload;
        dispatch({ type: 'SET_INE_FRONT_DATA', processId: payload.process_id, filename: payload.filename });
        dispatch({ type: 'INE_BACK_REQUIRED' });
        break;
      }

      case STREAM_EVENT_TYPE.SCORE_COMPLETE: {
        const payload = event.payload as unknown as ScorePayload;
        const score: DocumentScore = {
          score: payload.score,
          passed: payload.passed,
          details: payload.details,
        };

        // D-6: acumular el flag de revisión humana independientemente del tipo de documento.
        // Un score en rango HUMAN_REVIEW (75–84 por defecto) no rechaza, pero requiere
        // que un operador valide manualmente la sesión al finalizar el liveness.
        if (payload.requiresHumanReview) {
          dispatch({ type: 'ACCUMULATE_HUMAN_REVIEW' });
        }

        if (state.status === KYC_STATUS.VALIDATING_ID) {
          if (payload.passed) {
            // Persistir el output del classify para encadenar el pipeline tras liveness.
            // El campo details contiene el resultado crudo del workflow de clasificación.
            const details = payload.details as Record<string, unknown>;
            dispatch({
              type: 'SAVE_CLASSIFY_OUTPUT',
              processId: String(details.process_id ?? ''),
              filename: String(details.filename ?? ''),
              documents: Array.isArray(details.documents) ? details.documents : [],
            });
            dispatch({ type: 'ID_VALIDATION_SUCCESS', score, requiresPOA: payload.requiresPOA });
          } else {
            dispatch({ type: 'ID_VALIDATION_FAILED', reason: mapToRejectionReason(payload.rejectionReason) });
          }
        } else if (state.status === KYC_STATUS.VALIDATING_POA) {
          if (payload.score >= POA_SCORE_APPROVED && payload.passed) {
            // Score OK — guardar classify output y lanzar pipeline de validación completa.
            // La decisión final (SUCCESS/FAILED) llega como validation_complete o extraction_rejected.
            const details = payload.details as Record<string, unknown>;
            dispatch({
              type: 'SAVE_CLASSIFY_OUTPUT',
              processId: String(details.process_id ?? ''),
              filename: String(details.filename ?? ''),
              documents: Array.isArray(details.documents) ? details.documents : [],
            });
            dispatch({ type: 'POA_CLASSIFICATION_SUCCESS', score });
          } else if (payload.score >= POA_SCORE_CONDITIONAL) {
            dispatch({ type: 'POA_VALIDATION_CONDITIONAL', score });
          } else {
            dispatch({ type: 'POA_VALIDATION_FAILED', reason: mapToRejectionReason(payload.rejectionReason) });
          }
        }
        break;
      }

      case STREAM_EVENT_TYPE.EXTRACTION_COMPLETE: {
        // El pipeline server-side (execute_pipeline) emite este evento tras la extracción.
        // El encadenamiento a validation y storage lo hace el servidor — el frontend
        // solo persiste el output para referencia de depuración.
        const extractDetails = (event.payload.details ?? event.payload) as Record<string, unknown>;
        dispatch({
          type: 'SAVE_EXTRACTION_OUTPUT',
          processId: String(extractDetails.process_id ?? ''),
          filename: String(extractDetails.filename ?? ''),
          documents: Array.isArray(extractDetails.documents) ? extractDetails.documents : [],
        });
        break;
      }

      case STREAM_EVENT_TYPE.VALIDATION_COMPLETE:
        // D-7: si el pipeline está corriendo como gate de score, este evento indica que
        // extraction → validation completaron con éxito → avanzar al siguiente paso del flujo.
        if (state.status === KYC_STATUS.AWAITING_EXTRACTION_SCORE) {
          dispatch({ type: 'EXTRACTION_PIPELINE_SUCCESS' });
        } else if (state.status === KYC_STATUS.AWAITING_POA_PIPELINE) {
          // El pipeline de validación del POA completó — evaluar resultado de frescura.
          // Si el POA está vencido (> 3 meses) dispatch POA_VALIDATION_FAILED; si pasó, SUCCESS.
          const validationPayload = event.payload as { details?: Record<string, unknown> };
          const docValidations = (validationPayload.details?.document_validations ?? {}) as Record<string, unknown>;
          const poaResult = (docValidations.proof_of_address ?? {}) as { status?: string };
          if (poaResult.status === 'FAILED') {
            dispatch({ type: 'POA_VALIDATION_FAILED', reason: REJECTION_REASON.POA_TOO_OLD });
          } else {
            dispatch({
              type: 'POA_VALIDATION_SUCCESS',
              score: state.poaScore ?? { score: 0, passed: true, details: {} },
            });
          }
        }
        break;

      case STREAM_EVENT_TYPE.SCORE_UPDATE: {
        // Fix 2: score real del ai-service enviado por execute_pipeline tras la extracción.
        // Sobreescribe el score provisional del evento score_complete (que usaba approved_min como fallback).
        const scoreUpdate = event.payload as { score?: number };
        if (typeof scoreUpdate.score === 'number') {
          dispatch({ type: 'UPDATE_SCORE', score: scoreUpdate.score });
        }
        break;
      }

      case STREAM_EVENT_TYPE.EXTRACTION_REJECTED: {
        // El pipeline rechazó el documento. El motivo viene en el payload del backend.
        // En AWAITING_POA_PIPELINE el rechazo corresponde al POA (vencido, inválido, error)
        // y se resuelve como POA_VALIDATION_FAILED (reintentable). En cualquier otro estado
        // es un rechazo del documento de identidad y se resuelve como REJECT (terminal).
        const rejectedPayload = event.payload as { rejectionReason?: string };
        if (state.status === KYC_STATUS.AWAITING_POA_PIPELINE) {
          dispatch({
            type: 'POA_VALIDATION_FAILED',
            reason: mapToRejectionReason(rejectedPayload.rejectionReason),
          });
        } else {
          dispatch({
            type: 'REJECT',
            reason: mapToRejectionReason(rejectedPayload.rejectionReason),
          });
        }
        break;
      }

      case STREAM_EVENT_TYPE.WORKFLOW_ERROR:
        // Error técnico del pipeline server-side (Gemini caído, workflow timeout, etc.).
        // En AWAITING_EXTRACTION_SCORE: rechazo terminal del documento de identidad.
        // En AWAITING_POA_PIPELINE: fallo reintentable del POA (no bloquea definitivamente).
        // En cualquier otro estado: error de sesión sin transición de estado.
        if (state.status === KYC_STATUS.AWAITING_EXTRACTION_SCORE) {
          dispatch({ type: 'REJECT', reason: REJECTION_REASON.SERVICE_ERROR });
        } else if (state.status === KYC_STATUS.AWAITING_POA_PIPELINE) {
          dispatch({ type: 'POA_VALIDATION_FAILED', reason: REJECTION_REASON.SERVICE_ERROR });
        } else {
          dispatch({ type: 'SET_ERROR', message: 'Error en el procesamiento del documento. Intenta de nuevo.' });
        }
        break;

      case STREAM_EVENT_TYPE.SESSION_EXPIRED:
        dispatch({ type: 'REJECT', reason: REJECTION_REASON.SESSION_EXPIRED });
        break;

      default:
        break;
    }
  };

  // Suscripción al stream SSE del workflows-handler para recibir resultados en tiempo real.
  // En modo simulación se pasa sessionId especial para que el EventSource falle silenciosamente
  // (agota reintentos sin hacer ruido) — el stream simulado es quien emite los eventos reales.
  const { reconnecting: sseReconnecting } = useKYCStream({
    sessionId: simulation.enabled ? '__simulation__' : session.sessionId,
    token: simulation.enabled ? '' : session.token,
    apiBaseUrl: simulation.enabled ? '' : INTEGRATION_API_URL,
    onEvent: handleStreamEvent,
    onError: () => {
      // En modo simulación se ignora el error del SSE — es esperado que no conecte
      if (!simulation.enabled) {
        dispatch({ type: 'SET_ERROR', message: 'Se perdió la conexión con el servidor. Recarga la página.' });
      }
    },
  });

  // Stream simulado: emite eventos mock con delay artificial según el escenario activo.
  // Cuando la simulación está desactivada, onEvent es un no-op y el hook no hace nada.
  useSimulatedStream({
    status: state.status,
    scenario: simulation.scenario,
    onEvent: simulation.enabled ? handleStreamEvent : () => undefined,
  });

  // Notifica al padre sobre cambios de estado para el panel de debug en StandaloneMode
  useEffect(() => {
    onStateChange?.(state);
    // Se ejecuta en cada cambio de estado; onStateChange se estabiliza con useCallback en el padre
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Arranca el flujo automáticamente al montar el componente (sin userId) o tras USER_STATUS_RESOLVED
  useEffect(() => {
    if (state.status === KYC_STATUS.IDLE) {
      dispatch({ type: 'START_CAPTURE_ID' });
    }
  }, [state.status]);

  // D-7: verifica el estado KYC previo del usuario al iniciar el flujo con userId.
  // Determina si el usuario puede iniciar, está bloqueado o ya tiene un proceso completado.
  useEffect(() => {
    if (state.status !== KYC_STATUS.CHECKING_STATUS) return;

    const userId = state.session.userId;
    if (!userId) {
      dispatch({ type: 'USER_STATUS_RESOLVED' });
      return;
    }

    async function checkUserStatus(): Promise<void> {
      try {
        const userStatus = await getUserKycStatus(userId!, state.session.token);
        switch (userStatus.status) {
          case 'in_progress':
            // Si el proceso está esperando el comprobante de domicilio, retomar directo en POA.
            if (userStatus.flowStep === 'awaiting_poa') {
              dispatch({ type: 'RESUME_AT_POA' });
            } else {
              dispatch({ type: 'USER_STATUS_BLOCKED_ACTIVE', activeProcessId: userStatus.activeProcessId });
            }
            break;
          case 'human_review':
            dispatch({ type: 'USER_STATUS_BLOCKED_REVIEW', activeProcessId: userStatus.activeProcessId });
            break;
          case 'completed':
            dispatch({ type: 'USER_STATUS_COMPLETED', lastResult: userStatus.lastResult, canReset: userStatus.canReset });
            break;
          case 'awaiting_liveness':
            // Pipeline de documentos completado; el usuario debe continuar con la prueba de vida.
            dispatch({ type: 'RESUME_LIVENESS' });
            break;
          case 'rejected':
          case 'error':
            // Intento previo fallido — mostrar pantalla informativa; el usuario decide cuándo reintentar.
            dispatch({ type: 'USER_STATUS_PREVIOUS_FAILURE' });
            break;
          default: // 'none' → primera vez o estado limpio
            await startKycProcess(userId!, state.session.token);
            dispatch({ type: 'USER_STATUS_RESOLVED' });
        }
      } catch {
        // Si falla la consulta de estado, permitir continuar para no bloquear al usuario
        dispatch({ type: 'USER_STATUS_RESOLVED' });
      }
    }

    void checkUserStatus();
    // Solo se ejecuta al entrar a CHECKING_STATUS; session es estable durante el flujo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // D-7: dispara el pipeline server-side (extraction → gate de score → validation → storage)
  // en cuanto el documento pasa la clasificación. El servidor emitirá extraction_rejected
  // si el score es insuficiente o validation_complete si pasa — el frontend aguarda via SSE.
  useEffect(() => {
    if (state.status !== KYC_STATUS.AWAITING_EXTRACTION_SCORE || !state.classifyOutput) return;

    const { processId, filename, documents } = state.classifyOutput;
    executeWorkflow({
      action: 'pipeline',
      sessionId: state.session.sessionId,
      token: state.session.token,
      payload: {
        process_id: processId,
        filename,
        documents,
        kyc_status: state.status,
        // Indica al backend que el pasaporte requiere comprobante de domicilio posterior.
        // Con requires_poa=true el pipeline NO avanza a awaiting_liveness al terminar —
        // mantiene in_progress+flowStep:awaiting_poa para que el widget retome en POA.
        requires_poa: state.requiresPOA,
        // player_name: usado por el backend como fallback cuando la sesión en Firestore
        // no contiene player_data (simulación, testing). En producción el backend lo lee
        // de Firestore, pero enviarlo siempre garantiza consistencia en cualquier entorno.
        ...(state.session.playerName ? { player_name: state.session.playerName } : {}),
        ...(state.userId ? { user_id: state.userId } : {}),
      },
    }).catch(() => undefined);
    // Solo reacciona al cambio de status; classifyOutput se lee como snapshot del momento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // Dispara el pipeline de validación del POA (extraction → freshness → storage) cuando
  // el comprobante pasó la clasificación por score. La decisión final llega como
  // validation_complete o extraction_rejected — el resultado se interpreta en handleStreamEvent.
  useEffect(() => {
    if (state.status !== KYC_STATUS.AWAITING_POA_PIPELINE || !state.classifyOutput) return;

    const { processId, filename, documents } = state.classifyOutput;
    executeWorkflow({
      action: 'pipeline',
      sessionId: state.session.sessionId,
      token: state.session.token,
      payload: {
        process_id: processId,
        filename,
        documents,
        kyc_status: 'completed',
        ...(state.userId ? { user_id: state.userId } : {}),
      },
    }).catch(() => undefined);
    // Solo reacciona al cambio de status; classifyOutput es snapshot del momento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // D-6: llama al endpoint de revisión humana cuando el flujo transiciona a PENDING_HUMAN_REVIEW.
  // El error de red no bloquea al usuario — la pantalla ya está visible y el operador
  // puede consultar el estado en Firestore directamente si la notificación Pub/Sub falla.
  useEffect(() => {
    if (state.status === KYC_STATUS.PENDING_HUMAN_REVIEW) {
      submitHumanReview(state.session.sessionId, state.session.token).catch(() => undefined);
    }
    // Solo se dispara al entrar al estado terminal; session es estable durante el flujo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // Notifica al componente padre cuando el flujo termina
  useEffect(() => {
    const terminalStatuses: KYCStatus[] = [
      KYC_STATUS.COMPLETED,
      KYC_STATUS.REJECTED,
      KYC_STATUS.CONDITIONAL,
      KYC_STATUS.PENDING_HUMAN_REVIEW,
    ];
    if (terminalStatuses.includes(state.status) && onComplete) {
      onComplete(toKYCResult(state));
    }
    // Solo se dispara cuando cambia el estado; onComplete es estable entre renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // D-7: reinicia el proceso KYC completado — llama a la API y luego despacha RESET_KYC.
  // Solo disponible cuando state.canReset === true (ALLOW_KYC_RESET=true en el backend).
  async function handleReset(): Promise<void> {
    if (!state.userId) return;
    try {
      await resetKycProcess(state.userId, state.session.token);
      dispatch({ type: 'RESET_KYC' });
    } catch {
      dispatch({ type: 'SET_ERROR', message: 'Error al reiniciar el proceso KYC. Intenta de nuevo.' });
    }
  }

  // Llamado desde PreviousFailureScreen cuando el usuario decide reintentar tras un fallo previo.
  // Crea la sesión KYC nueva antes de activar el autostart, igual que el flujo normal de CHECKING_STATUS.
  async function handleContinueAfterFailure(): Promise<void> {
    if (!state.userId) return;
    try {
      await startKycProcess(state.userId, state.session.token);
    } catch {
      // Si falla la creación, el flujo continúa de todos modos — el backend reintenta al ejecutar.
    }
    dispatch({ type: 'USER_STATUS_RESOLVED' });
  }

  // D-8: el polling detectó que el proceso in_progress cambió de estado.
  // Reutiliza las mismas acciones de D-7 para las transiciones, manteniendo la FSM consistente.
  function handleResumeStatusChange(response: UserKycStatusResponse): void {
    switch (response.status) {
      case 'completed':
        dispatch({ type: 'USER_STATUS_COMPLETED', lastResult: response.lastResult, canReset: response.canReset });
        break;
      case 'awaiting_liveness':
        // El pipeline de documentos completó pero el usuario cerró el widget antes de la prueba de vida.
        // Ir directo al paso de liveness (LivenessStep inicia en el guide/preview).
        dispatch({ type: 'RESUME_LIVENESS' });
        break;
      case 'in_progress':
        // El pipeline del pasaporte terminó y está esperando que el usuario suba el comprobante.
        // El backend restaura flowStep="awaiting_poa" tras el storage cuando requires_poa=True.
        // Si el flowStep no está seteado, el pipeline aún sigue corriendo — seguir esperando.
        if (response.flowStep === 'awaiting_poa') {
          dispatch({ type: 'RESUME_AT_POA' });
        }
        break;
      case 'human_review':
        dispatch({ type: 'USER_STATUS_BLOCKED_REVIEW', activeProcessId: response.activeProcessId });
        break;
      default:
        // 'rejected', 'none', 'error' o cualquier estado inesperado → permitir nueva captura
        dispatch({ type: 'USER_STATUS_RESOLVED' });
    }
  }

  // D-8: el polling alcanzó el timeout sin cambio de estado — dar por terminada la espera.
  function handleResumeTimeout(): void {
    dispatch({ type: 'RESUME_GIVE_UP' });
  }

  // D-8: sondea el estado del proceso in_progress mientras el frontend está en RESUMING_SESSION.
  // Desactivado en todos los demás estados para no generar llamadas innecesarias.
  useResumePolling({
    userId: state.userId ?? '',
    token: state.session.token,
    enabled: state.status === KYC_STATUS.RESUMING_SESSION && !!state.userId,
    onStatusChange: handleResumeStatusChange,
    onTimeout: handleResumeTimeout,
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Barra de progreso del flujo */}
      <LinearProgress
        variant="determinate"
        value={getProgress(state.status)}
        sx={{ borderRadius: 0, height: 4, mb: 0 }}
      />

      {/* D-8: indicador de reconexión SSE — barra ámbar animada mientras se reintenta */}
      {sseReconnecting && !simulation.enabled && (
        <LinearProgress
          variant="indeterminate"
          sx={{
            borderRadius: 0,
            height: 2,
            mb: 0,
            backgroundColor: 'warning.light',
            '& .MuiLinearProgress-bar': { backgroundColor: 'warning.main' },
          }}
        />
      )}

      {/* Contenido del paso actual */}
      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {renderStep(state, dispatch, onDismiss, handleReset, handleContinueAfterFailure)}
      </Box>
    </Box>
  );
}

// Renderiza el componente correspondiente al estado actual de la máquina
function renderStep(
  state: KYCFlowState,
  dispatch: Dispatch<KYCAction>,
  onDismiss?: () => void,
  onReset?: () => void,
  onContinueAfterFailure?: () => void,
): ReactNode {
  switch (state.status) {
    case KYC_STATUS.IDLE:
      return null;

    // D-7: pantalla de espera mientras se verifica el estado KYC previo del usuario
    case KYC_STATUS.CHECKING_STATUS:
      return <ValidationProgress message="Verificando tu estado de verificación" />;

    case KYC_STATUS.CAPTURING_ID:
      return <CaptureIDStep state={state} dispatch={dispatch} />;

    case KYC_STATUS.VALIDATING_ID:
      return <ValidationProgress message="Verificando tu identificación" />;

    case KYC_STATUS.CAPTURING_INE_BACK:
      return <CaptureINEBackStep state={state} dispatch={dispatch} />;

    // D-7: pantalla de espera mientras el pipeline backend valida el score del documento
    case KYC_STATUS.AWAITING_EXTRACTION_SCORE:
      return (
        <ValidationProgress
          message="Validando tu documento, esto puede tomar 1-2 minutos"
          hint="Puedes cerrar esta ventana — tu proceso continuará en segundo plano y podrás regresar en cualquier momento para ver el resultado."
        />
      );

    case KYC_STATUS.CAPTURING_POA:
      return <CapturePOAStep state={state} dispatch={dispatch} />;

    case KYC_STATUS.VALIDATING_POA:
      return <ValidationProgress message="Verificando tu comprobante de domicilio" />;

    // Pipeline de validación completa del POA corriendo en el servidor (extraction + freshness)
    case KYC_STATUS.AWAITING_POA_PIPELINE:
      return (
        <ValidationProgress
          message="Validando tu comprobante de domicilio, esto puede tomar un momento"
          hint="Verificando que el comprobante sea reciente y provenga de una fuente válida."
        />
      );

    case KYC_STATUS.CAPTURING_LIVENESS:
      return <LivenessStep state={state} dispatch={dispatch} />;

    case KYC_STATUS.COMPLETED:
      return <CompletionScreen state={state} onDismiss={onDismiss} />;

    // D-7: el usuario ya tiene un proceso KYC completado — muestra resultado anterior
    case KYC_STATUS.COMPLETED_PREVIOUS:
      return <CompletedPreviousScreen state={state} onDismiss={onDismiss} onReset={state.canReset ? onReset : undefined} />;

    case KYC_STATUS.REJECTED:
      return <RejectionScreen state={state} dispatch={dispatch} onDismiss={onDismiss} />;

    case KYC_STATUS.CONDITIONAL:
      return <ConditionalScreen state={state} onDismiss={onDismiss} />;

    case KYC_STATUS.PENDING_HUMAN_REVIEW:
      return <HumanReviewScreen state={state} onDismiss={onDismiss} />;

    // D-7: el proceso del usuario está bloqueado en revisión humana
    case KYC_STATUS.USER_IN_HUMAN_REVIEW:
      return <UserInHumanReviewScreen onDismiss={onDismiss} />;

    // D-8: proceso in_progress previo — esperando resolución del pipeline server-side
    case KYC_STATUS.RESUMING_SESSION:
      return (
        <ResumingSessionScreen
          onGiveUp={() => dispatch({ type: 'RESUME_GIVE_UP' })}
        />
      );

    // Intento anterior terminó en rejected/error — informar al usuario antes de reiniciar.
    case KYC_STATUS.PREVIOUS_FAILURE:
      return <PreviousFailureScreen onContinue={onContinueAfterFailure ?? (() => {})} />;

    default:
      return null;
  }
}

// Re-exportar tipos para que los steps los importen desde un único lugar
export type { KYCFlowState, KYCAction };

// Exportar funciones internas solo para testing — no forman parte de la API pública
export { kycReducer, buildInitialState };
